// sparepart.service — guard izin + orkestrasi (request → serah → modal, hutang).
import { currentUser } from '@core/session';
import { pushNotif } from '@core/notif';
import { logAudit } from '@core/audit';
import { kebijakan } from '@core/settings';
import { wa62, isoOf } from '@domain/format';
import { outstanding, serahTotal, paidTotal, type SerahItem } from '@domain/sparepart';
import * as repo from './sparepart.repo';
import { bisaUbah } from '@core/perms';
import { tambahMutasi, listAkun } from '@modules/payments/akunkas.repo';
import { akunUntukCara } from '@domain/akunkas';

const role = (): string => currentUser()?.role ?? '';
const nama = (): string => currentUser()?.nama ?? '';

/** Teknisi hanya boleh menyentuh tiket yang ditugaskan padanya (port v0.18). */
async function assertOwnTicket(no: string): Promise<string> {
  const t = await repo.ticketInfo(no);
  if (role() === 'Teknisi' && t.teknisi !== nama()) throw new Error('Tiket ini bukan penugasan Anda.');
  return t.status;
}

// Peran + Hak Akses (Pengaturan → Supplier). Guard ada DI SERVICE, bukan cuma di tombol:
// "lihat saja" harus menolak walau tombolnya berhasil dipanggil dari mana pun.
export const canManageSup = (): boolean => ['Owner', 'Manager'].includes(role()) && bisaUbah('pengaturan.sup_sp.supplier.tambah');
export const canDeleteSup = (): boolean => ['Owner', 'Manager'].includes(role()) && bisaUbah('pengaturan.sup_sp.supplier.hapus');
export const canManageSparepart = (): boolean => ['Owner', 'Manager', 'SPV'].includes(role());
export const canRequestPart = (): boolean => ['Owner', 'Manager', 'SPV', 'Teknisi'].includes(role()) && bisaUbah('sparepart.request');
// Ajukan retur boleh +CS (approval supplier jadi pengaman) — port keputusan Apo. Teknisi via assertOwnTicket.
export const canReturRequest = (): boolean => ['Owner', 'Manager', 'SPV', 'CS', 'Teknisi'].includes(role()) && bisaUbah('sparepart.retur');
export const canSettleSup = (): boolean => ['Owner', 'Manager', 'Admin Finance'].includes(role());

// ---- Supplier master ----
export interface SupForm { nama: string; wa: string; kota: string; alamat: string; rekening: string; outlet: string | null; aktif: boolean }
export async function saveSupplier(id: number, f: SupForm): Promise<void> {
  if (!canManageSup()) throw new Error('Anda tidak punya hak mengubah data supplier.');
  if (!f.nama.trim()) throw new Error('Nama supplier wajib diisi.');
  const d = { nama: f.nama.trim(), wa: wa62(f.wa) || null, kota: f.kota.trim() || null, alamat: f.alamat.trim() || null, rekening: f.rekening.trim() || null, outlet: f.outlet, aktif: f.aktif };
  if (id) await repo.updateSupplier(id, d); else await repo.createSupplier(d);
}
export async function deleteSupplier(id: number): Promise<void> {
  if (!canDeleteSup()) throw new Error('Anda tidak punya hak menghapus supplier.');
  // sparepart_requests.supplier_id = NO ACTION → jaga jejak keuangan (hutang/serah); katalog spareparts ON DELETE CASCADE.
  if ((await repo.requestCountForSupplier(id)) > 0)
    throw new Error('Supplier masih punya riwayat request/hutang sparepart — tidak bisa dihapus (jejak keuangan dijaga).');
  await repo.removeSupplier(id);
}

// ---- Sparepart master ----
export interface SpForm { supplier_id: number; barcode: string; nama: string; kategori: string; stok: number; harga: number }
export async function saveSparepart(id: number, f: SpForm): Promise<void> {
  if (!canManageSparepart()) throw new Error('Kelola sparepart hanya Owner/Manager/SPV.');
  const bc = f.barcode.trim();
  if (!bc || !f.nama.trim()) throw new Error('Kode / barcode & nama wajib diisi.'); // barcode = kunci stok (v0.18)
  if (!f.supplier_id) throw new Error('Pilih supplier.');
  if (f.stok < 0 || f.harga < 0) throw new Error('Nilai tidak valid.');
  // Barcode WAJIB unik per supplier (cegah decrement_stock kena banyak baris / modal tanpa stok). v0.18 cek-dup create; kita cek create+edit.
  const existing = await repo.barcodesForSupplier(f.supplier_id);
  if (existing.some((x) => x.id !== id && (x.barcode ?? '').toLowerCase() === bc.toLowerCase()))
    throw new Error('Kode / barcode sudah ada untuk supplier ini.');
  const d = { supplier_id: f.supplier_id, barcode: bc, nama: f.nama.trim(), kategori: f.kategori.trim() || null, stok: f.stok, harga: f.harga };
  if (id) await repo.updateSparepart(id, d); else await repo.createSparepart(d);
}
export async function deleteSparepart(id: number): Promise<void> {
  if (!canManageSparepart()) throw new Error('Hapus sparepart hanya Owner/Manager/SPV.');
  await repo.removeSparepart(id);
}

// ---- Request → Serah → Modal ----
export async function requestPart(ticketNo: string, outlet: string | null, supplierId: number | null, supplierNama: string | null, items: string[], opts?: { indent?: boolean; foto?: string[] }): Promise<void> {
  if (!canRequestPart()) throw new Error('Tidak punya izin request sparepart.');
  if (!items.length) throw new Error('Isi minimal 1 barang.');
  const st = await assertOwnTicket(ticketNo);
  if (st !== 'Dikerjakan') throw new Error('Request sparepart hanya untuk tiket berstatus Dikerjakan.'); // B4: selaras UI (bukan Diagnosa)
  await repo.createRequest({ ticket_no: ticketNo, outlet, supplier_id: supplierId, supplier_nama: supplierNama, items, by_user: nama(), indent: !!opts?.indent, foto: opts?.foto ?? [] });
  // INDENT: barang belum ada di supplier mana pun. Diberi tahu supaya CS punya jawaban untuk
  // pelanggan — tiket yang menunggu barang tampak "mandek" kalau tak ada keterangannya.
  if (opts?.indent) {
    try { await pushNotif('CS', outlet, `⏳ INDENT: ${ticketNo} menunggu barang dipesan${supplierNama ? ` (${supplierNama})` : ''} — kabari pelanggan bila perlu.`, ticketNo); } catch { /* best-effort */ }
  }
  // LINTAS CABANG: dipakai saat supplier cabang sendiri kosong (keputusan Apo 29 Jul). Tak perlu
  // persetujuan supaya pekerjaan tak tertahan, tapi atasan WAJIB tahu — ada ongkos kirim &
  // koordinasi antar cabang di baliknya. Tak butuh kolom baru: cukup dibandingkan cabang supplier
  // dengan cabang tiket. Best-effort — notif gagal tak boleh membatalkan request yang sudah masuk.
  try {
    if (!supplierId || !outlet) return;
    const sup = (await repo.listSuppliers()).find((x) => x.id === supplierId);
    if (sup?.outlet && sup.outlet !== outlet) {
      await pushNotif('SPV', outlet, `🔄 Request LINTAS CABANG: ${ticketNo} (${outlet}) minta part ke ${sup.nama} — supplier cabang ${sup.outlet}. Ongkos kirim dicatat di Beban.`, ticketNo);
    }
  } catch { /* notif opsional */ }
}

export const canSerah = (): boolean => role() === 'Supplier' && bisaUbah('sup_req.serah');

/** Konfirmasi penerimaan = staf iFiCs, BUKAN supplier (itu inti gunanya konfirmasi ini). */
export const canTerimaPart = (): boolean => ['Owner', 'Manager', 'SPV', 'CS', 'Teknisi'].includes(role()) && bisaUbah('sparepart.terima');

/**
 * Teknisi/staf mengonfirmasi barang SUDAH DITERIMA → modal tiket & hutang supplier baru diakui
 * di titik ini (keputusan Apo 30 Jul). Aturan siapa-boleh ditegakkan RPC (0128); guard di sini
 * supaya tombol yang salah muncul pun tak bisa menembus.
 */
export async function terimaPart(reqId: number, ticketNo: string, ket = ''): Promise<void> {
  if (!canTerimaPart()) throw new Error('Konfirmasi penerimaan hanya oleh teknisi/staf iFiCs.');
  await assertOwnTicket(ticketNo);
  await repo.terimaSparepart(reqId, ket);
}

/** Serah barang (SUPPLIER SAJA — port v0.18 supplier-serah). Simpan serah + kurangi stok + modal.
 *  items boleh qty 0 (tidak ready) → dicatat tapi tak dihitung; wajib ≥1 ready (qty>0). */
export async function serahPart(reqId: number, ticketNo: string, _supplierId: number | null, items: SerahItem[]): Promise<void> {
  if (!canSerah()) throw new Error('Serah sparepart hanya oleh Supplier (dari portal).');
  const req = await repo.getRequest(reqId);
  if (!req) throw new Error('Request tidak ditemukan.');
  if (req.serah) throw new Error('Request ini sudah diserahkan.');
  const supplierId = req.supplier_id; // pakai supplier target request (bukan input klien)
  const info = await repo.ticketMetaSupplier(ticketNo); // RPC aman (supplier tak baca penuh tickets)
  if (['Siap Diambil', 'Sudah Diambil'].includes(info.status)) throw new Error('Tidak bisa serah sparepart setelah tiket Siap/Sudah Diambil.');
  const ready = items.filter((it) => it.qty > 0);
  if (!ready.length) throw new Error('Isi minimal 1 barang READY (qty>0). Barang tidak ready boleh dicatat (qty 0 + keterangan).');
  // Barcode WAJIB TERISI untuk tiap barang yang diserahkan (Apo 30 Jul) — terlepas dari kebijakan
  // "wajib terdaftar di katalog" di bawah, yang bisa dimatikan. Dulu layar mengarang barcode dari
  // nama barang saat kosong, sehingga stok supplier tak pernah berkurang (barcode karangan tak ada
  // di katalog) dan kolom barcode katalog ikut tercemar.
  const tanpaBc = ready.find((it) => !it.barcode?.trim());
  if (tanpaBc) throw new Error(`Barcode wajib untuk barang yang diserahkan — "${tanpaBc.nama || '—'}" belum ada barcode-nya.`);
  // Kebijakan: serah WAJIB barcode terdaftar di katalog supplier (Pengaturan → Kebijakan) — hanya barang ready.
  if (kebijakan().serahWajibBarcode && supplierId) {
    const cat = new Set((await repo.barcodesForSupplier(supplierId)).map((x) => (x.barcode ?? '').toLowerCase()).filter(Boolean));
    const bad = ready.find((it) => !it.barcode?.trim() || !cat.has(it.barcode.trim().toLowerCase()));
    if (bad) throw new Error(`Serah wajib barcode terdaftar (Pengaturan): "${bad.nama || bad.barcode || '—'}" belum ada di katalog Anda.`);
  }
  await repo.serahRequest(reqId, { items, at: new Date().toISOString(), by: nama() });
  if (supplierId) for (const it of ready) { if (it.barcode) await repo.reduceStock(supplierId, it.barcode, it.qty); }
  await repo.recomputeTicketModal(ticketNo);
  // Notif next-actor (best-effort): part datang → teknisi lanjut kerjakan. Port serah v0.18.
  try { if (info.teknisi) await pushNotif('TEK:' + info.teknisi, req.outlet, `🔩 Sparepart untuk ${ticketNo} sudah diserahkan supplier — silakan lanjut kerjakan.`, ticketNo); }
  catch { /* notif best-effort */ }
}

/**
 * Batalkan request yang BELUM diserah. Guard di service (UI hanya menyembunyikan tombol saat sudah
 * diserah — modal usang / panggilan langsung bisa menembusnya saat supplier menyerahkan bersamaan).
 * Barang yang sudah diserah WAJIB lewat retur: batal-langsung menurunkan modal TANPA mengembalikan
 * stok supplier, dan menghilangkan hutang dari daftar payables (`.eq('batal', false)`).
 * Sejalan dgn penjaga requestRetur/ackRetur.
 */
export async function cancelRequest(reqId: number, ticketNo: string): Promise<void> {
  if (!canRequestPart()) throw new Error('Tidak punya izin membatalkan request.');
  await assertOwnTicket(ticketNo);
  const req = await repo.getRequest(reqId);
  if (!req) throw new Error('Request tidak ditemukan.');
  if (req.batal) throw new Error('Request ini sudah dibatalkan/diretur.');
  if (req.serah) throw new Error('Barang sudah diserahkan supplier — gunakan "Ajukan Retur ke supplier" agar stok & hutang ikut dikembalikan, bukan Batalkan.');
  if (req.payments && req.payments.length) throw new Error('Sudah ada pembayaran ke supplier — batalkan pembayaran di Hutang Supplier dulu.');
  await repo.setBatalRequest(reqId, true);
  await repo.recomputeTicketModal(ticketNo);
}

// ---- Retur 2-langkah (staf ajukan → supplier terima/tolak). Port v0.18. ----
/** Staf AJUKAN retur (all-or-nothing: seluruh serah). Status 'pending' → modal BELUM berubah (konservatif). */
/**
 * Ajukan retur. `items` kosong = seluruh serah (perilaku lama); diisi = retur SEBAGIAN
 * (permintaan Apo: "ada 2 barang, bisa pilih salah 1 atau keduanya").
 *
 * Batasnya bukan lagi "satu retur per request", melainkan per-BARANG: barang yang returnya masih
 * menunggu atau sudah diterima tak boleh diajukan lagi. Kalau dibatasi per-request seperti dulu,
 * retur sebagian yang pertama akan mengunci barang lain selamanya.
 */
export async function requestRetur(reqId: number, ticketNo: string, items?: SerahItem[]): Promise<void> {
  if (!canReturRequest()) throw new Error('Ajukan retur hanya oleh staf iFiCs (O/M/SPV/CS/Teknisi).');
  await assertOwnTicket(ticketNo);
  const req = await repo.getRequest(reqId);
  if (!req || !req.serah) throw new Error('Request belum diserah / tidak ditemukan.');
  if (req.batal) throw new Error('Request ini sudah dibatalkan/diretur.');
  if (req.payments && req.payments.length) throw new Error('Sudah ada pembayaran ke supplier — batalkan lewat Hutang Supplier dulu.');
  if (req.serah.retur?.some((r) => r.status === 'pending')) throw new Error('Masih ada retur menunggu jawaban supplier — tunggu itu diputuskan dulu.');

  const kunci = (it: { barcode?: string; nama?: string }): string => it.barcode || it.nama || '';
  const dipilih = (items?.length ? items : req.serah.items).filter((it) => it.qty > 0);
  if (!dipilih.length) throw new Error('Pilih minimal 1 barang yang diretur.');
  // Hanya barang yang benar-benar ada di serah ini, dan yang belum pernah diretur/ditolak.
  for (const it of dipilih) {
    const asli = req.serah.items.find((x) => kunci(x) === kunci(it));
    if (!asli) throw new Error(`"${it.nama}" tidak ada di daftar serah request ini.`);
    const sudah = (req.serah.retur ?? []).some((rv) => (rv.items ?? []).some((x) => kunci(x) === kunci(it)));
    if (sudah) throw new Error(`"${it.nama}" sudah pernah diretur — tak bisa diajukan dua kali.`);
    if (it.qty > asli.qty) throw new Error(`Jumlah retur "${it.nama}" melebihi yang diserahkan.`);
  }
  const retur = [...(req.serah.retur ?? []), { at: new Date().toISOString(), by: nama(), items: dipilih, status: 'pending' as const }];
  await repo.serahRequest(reqId, { ...req.serah, retur }); // batal tetap false → modal tak berubah (menunggu supplier)
  // Supplier melihat retur menunggu di portal (Request Masuk); belum ada lonceng khusus supplier.
}

/** Supplier TERIMA retur → restore stok + batal + modal turun. */
export async function ackRetur(reqId: number, ticketNo: string): Promise<void> {
  if (role() !== 'Supplier') throw new Error('Konfirmasi retur hanya oleh Supplier.');
  const req = await repo.getRequest(reqId);
  if (!req || !req.serah) throw new Error('Request tidak ditemukan.');
  // #1: retur yang sudah dibayar Finance tak boleh diterima di sini (kas nyangkut) — batalkan pembayaran dulu.
  if (req.payments && req.payments.length) throw new Error('Request ini sudah dibayar Finance — batalkan pembayaran di Hutang Supplier sebelum menerima retur.');
  const idx = req.serah.retur?.findIndex((r) => r.status === 'pending') ?? -1;
  if (idx < 0) throw new Error('Tidak ada retur menunggu.');
  const ev = req.serah.retur![idx]!;
  const retur = req.serah.retur!.map((r, i) => i === idx ? { ...r, status: 'diterima' as const, decidedBy: nama(), decidedAt: new Date().toISOString() } : r);
  // Stok dikembalikan HANYA untuk barang yang benar-benar diretur — dulu seluruh serah
  // dikembalikan, dan itu salah begitu retur boleh sebagian (stok supplier jadi kelebihan).
  if (req.supplier_id) for (const it of (ev.items ?? [])) { if (it.barcode && it.qty > 0) await repo.restoreStock(req.supplier_id, it.barcode, it.qty); }
  // `batal` hanya untuk retur PENUH. Kalau sebagian, request tetap hidup: barang yang tidak
  // diretur masih terpakai dan tetap harus masuk modal + tetap jadi hutang ke supplier.
  const kunci2 = (it: { barcode?: string; nama?: string }): string => it.barcode || it.nama || '';
  const returSeluruh = req.serah.items.every((it) => {
    const q = retur.filter((rv) => rv.status !== 'pending' && rv.status !== 'ditolak')
      .reduce((a, rv) => a + (rv.items ?? []).reduce((b, x) => b + (kunci2(x) === kunci2(it) ? x.qty : 0), 0), 0);
    return q >= it.qty;
  });
  if (returSeluruh) await repo.returAndBatal(reqId, { ...req.serah, retur });
  else await repo.serahRequest(reqId, { ...req.serah, retur });
  await repo.recomputeTicketModal(ticketNo);   // migrasi 0130 mengurangi qty yang returnya diterima
  try { const info = await repo.ticketMetaSupplier(ticketNo); if (info.teknisi) await pushNotif('TEK:' + info.teknisi, req.outlet, `✅ Retur (Tiket ${ticketNo}) DITERIMA supplier — stok & modal dikembalikan.`, ticketNo); }
  catch { /* notif best-effort */ }
}

/** Supplier TOLAK retur → part TETAP terpakai (masuk modal) + alasan wajib + kerugian teknisi + notif. */
export async function rejectRetur(reqId: number, ticketNo: string, reason: string): Promise<void> {
  if (role() !== 'Supplier') throw new Error('Konfirmasi retur hanya oleh Supplier.');
  if (!reason.trim()) throw new Error('Alasan penolakan wajib diisi.');
  const req = await repo.getRequest(reqId);
  if (!req || !req.serah) throw new Error('Request tidak ditemukan.');
  const idx = req.serah.retur?.findIndex((r) => r.status === 'pending') ?? -1;
  if (idx < 0) throw new Error('Tidak ada retur menunggu.');
  const retur = req.serah.retur!.map((r, i) => i === idx ? { ...r, status: 'ditolak' as const, ket: reason.trim(), decidedBy: nama(), decidedAt: new Date().toISOString() } : r);
  await repo.serahRequest(reqId, { ...req.serah, retur }); // batal tetap false → part tetap di modal
  try { const info = await repo.ticketMetaSupplier(ticketNo); if (info.teknisi) await pushNotif('TEK:' + info.teknisi, req.outlet, `⚠️ Retur (Tiket ${ticketNo}) DITOLAK supplier: ${reason.trim()}. Part dihitung terpakai (masuk modal Anda).`, ticketNo); }
  catch { /* notif best-effort */ }
}

// ---- Hutang Supplier ----
export async function paySupplier(reqId: number, outlet: string | null, amount: number, method: string, note: string): Promise<{ akunTerupdate: boolean }> {
  if (!canSettleSup()) throw new Error('Pelunasan hutang supplier hanya Owner/Manager/Finance.');
  if (amount <= 0) throw new Error('Jumlah tidak valid.');
  // Hitung ulang sisa SEGAR dari DB (jangan percaya snapshot client). DB trigger backstop overpay.
  const req = await repo.getRequest(reqId);
  if (!req) throw new Error('Data hutang tidak ditemukan.');
  const sisa = outstanding(serahTotal(req.serah), paidTotal(req.payments));
  if (amount > sisa) throw new Error(`Jumlah melebihi sisa hutang (Rp ${sisa.toLocaleString('id-ID')}).`);
  await repo.addPayment({ req_id: reqId, outlet: req.outlet ?? outlet, amount, method, note, by_user: nama() });
  // Catat KELUAR KAS dari akun sesuai metode → saldo laci/rekening cabang berkurang, sehingga
  // Neraca (Posisi Keuangan) seimbang otomatis (HPP yang keluar kini tercermin di akun, tak lagi
  // muncul sebagai "Belum ditempel ke akun"). Best-effort: bila metode tak cocok akun mana pun atau
  // insert gagal, pembayaran hutang tetap tercatat — jangan gagalkan aksi yang sudah berhasil.
  // Catatan rekonsiliasi: derived Kas mengurangi hppPaid (= hpp recognized − hutangSupplier), sedang
  // akun mengurangi pembayaran AKTUAL. Cocok saat tiket ber-sparepart sudah diakui (modal=serahTotal
  // via recomputeTicketModal); selisih waktu-pengakuan jatuh ke "Selisih lain" di rincian Neraca.
  let akunTerupdate = false;
  try {
    const akun = akunUntukCara(method, await listAkun(), req.outlet ?? outlet);
    if (akun) {
      await tambahMutasi({
        tgl: isoOf(new Date()), akunId: akun.id, arah: 'keluar', jumlah: amount, jenis: 'supplier',
        catatan: `Bayar supplier ${req.supplier_nama ?? ''} · ${req.ticket_no}`.trim(),
        refTabel: 'sparepart_requests', refId: String(reqId), outlet: req.outlet ?? outlet,
      });
      akunTerupdate = true;
    }
  } catch { /* mutasi kas best-effort — hutang sudah tercatat di req_payments */ }
  // §26: pelunasan hutang = aksi uang → jejak audit (best-effort).
  void logAudit({ action: 'SUP_PAYMENT', module: 'sparepart', refId: req.ticket_no, outlet: req.outlet ?? outlet,
    after: { supplier: req.supplier_nama, amount, method, note, sisaSebelum: sisa } });
  return { akunTerupdate };
}

/** Supplier mengisi perkiraan tanggal barang indent tersedia → teknisi/CS diberi tahu. */
export async function setIndentEta(reqId: number, eta: string, note: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const req = await repo.getRequest(reqId); if (!req) throw new Error('Request tidak ditemukan.');
  if (!req.indent) throw new Error('Request ini bukan pesanan indent.');
  await repo.setIndentEta(reqId, eta || null, note.trim());
  try {
    await pushNotif('CS', req.outlet, `📦 ${req.supplier_nama ?? 'Supplier'} memperbarui pesanan indent ${req.ticket_no}${eta ? ` — perkiraan tersedia ${eta}` : ''}${note.trim() ? ` (${note.trim()})` : ''}.`, req.ticket_no);
    const t = await repo.ticketMetaSupplier(req.ticket_no);
    if (t.teknisi) await pushNotif('TEK:' + t.teknisi, req.outlet, `📦 Pesanan indent ${req.ticket_no}${eta ? ` diperkirakan tersedia ${eta}` : ' diperbarui supplier'}.`, req.ticket_no);
  } catch { /* best-effort */ }
}

/** Hapus satu foto part dari request (berkas + daftarnya). */
export async function hapusFotoRequest(reqId: number, path: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  const req = await repo.getRequest(reqId); if (!req) throw new Error('Request tidak ditemukan.');
  const sisa = (req.foto ?? []).filter((f) => f !== path);
  await repo.setFotoRequest(reqId, sisa);
  const { deleteFile } = await import('@core/storage');
  try { await deleteFile('sparepart', path); } catch { /* berkas mungkin sudah hilang — daftarnya yang penting */ }
}

/**
 * Supplier menjawab "barang kosong". Request ditandai indent + keterangan, TIDAK dibatalkan —
 * kalau dibatalkan, jejak permintaannya hilang dan teknisi harus mengulang dari nol. Teknisi & CS
 * langsung diberi tahu supaya bisa mencari ke supplier lain atau mengabari pelanggan.
 */
export async function tandaiKosong(reqId: number, ticketNo: string, ket: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  await repo.setIndentKosong(reqId, ket);
  try {
    const t = await repo.ticketMetaSupplier(ticketNo);
    const req = await repo.getRequest(reqId);
    const pesan = `📭 ${req?.supplier_nama ?? 'Supplier'}: barang untuk ${ticketNo} KOSONG — ${ket}`;
    await pushNotif('CS', req?.outlet ?? null, pesan, ticketNo);
    if (t.teknisi) await pushNotif('TEK:' + t.teknisi, req?.outlet ?? null, `${pesan}. Cari di supplier cabang lain atau tunggu indent.`, ticketNo);
  } catch { /* best-effort */ }
}

/**
 * Supplier MENOLAK melanjutkan indent (barang memang tak bisa diadakan). Request dibatalkan supaya
 * tak menggantung selamanya, tapi alasannya ikut tercatat & teknisi/CS diberi tahu agar bisa segera
 * mencari ke supplier lain — menolak diam-diam berarti tiket menunggu barang yang tak akan datang.
 */
export async function tolakIndent(reqId: number, alasan: string): Promise<void> {
  const u = currentUser(); if (!u) throw new Error('Sesi tidak valid.');
  if (!alasan.trim()) throw new Error('Alasan wajib diisi.');
  const req = await repo.getRequest(reqId); if (!req) throw new Error('Request tidak ditemukan.');
  await repo.tolakIndent(reqId, alasan.trim());
  try {
    const t = await repo.ticketMetaSupplier(req.ticket_no);
    const pesan = `🚫 ${req.supplier_nama ?? 'Supplier'} TIDAK bisa mengadakan barang ${req.ticket_no} — ${alasan.trim()}`;
    await pushNotif('CS', req.outlet, pesan, req.ticket_no);
    if (t.teknisi) await pushNotif('TEK:' + t.teknisi, req.outlet, `${pesan}. Coba supplier cabang lain.`, req.ticket_no);
  } catch { /* best-effort */ }
}
